import React, { useEffect, useState } from "react";
import "./LookToBook.css";
import axios from "axios";
import dayjs from "dayjs";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { Chart } from "react-google-charts";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import Loader from "../../Components/Loader/Loader";
import downloadSvg from "../../Assets/download.svg";

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    paddingRight: "30px",
    borderColor: "#ddd",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#EF5443",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#EF5443",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
  },
  "& .MuiSelect-icon": {
    color: "#f44336",
  },
}));

const CustomDatePicker = styled(DatePicker)({
  "& .MuiInputBase-root": {
    width: "250px",
    height: "55px",
    paddingRight: "14px !important",
    overflow: "hidden !important",
  },
  "& .MuiInputBase-input": {
    color: "#ef5443",
    fontSize: "15px",
    overflow: "hidden !important",
  },
  "& .MuiInputAdornment-root": {
    marginRight: "0 !important",
  },
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#334555",
    color: theme.palette.common.white,
    fontWeight: 500,
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
}));

const PlaceholderTypography = styled("div")(({ theme }) => ({
  color: "#888",
  fontSize: "16px",
}));

const AIRLINE_PALETTE = [
  "#EF5443",
  "#334555",
  "#4C9F70",
  "#F2B134",
  "#7B61FF",
  "#00A8CC",
  "#E85D9C",
  "#2E8B8B",
];

const getAirlineColor = (code, airlineCodes) => {
  const idx = airlineCodes.indexOf(code);
  if (idx === -1) return AIRLINE_PALETTE[0];
  return AIRLINE_PALETTE[idx % AIRLINE_PALETTE.length];
};

const QUICK_RANGES = [
  { label: "Last 7 Days", days: 7 },
  { label: "Last 14 Days", days: 14 },
  { label: "Last 30 Days", days: 30 },
  { label: "Last 90 Days", days: 90 },
];

const roundValue = (value) => {
  if (value === null || value === undefined) return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return Number(num > 0 ? num.toFixed(2) : num.toFixed(3));
};

const formatNumber = (value, decimals) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 0,
  });
};

const displayMetric = (value, decimals) => {
  if (value === null || value === undefined || value === 0) return "-";
  return formatNumber(value, decimals);
};

const filterSectors = (sectors) =>
  (sectors || [])
    .filter((s) => s.totalRequests >= 10)
    .sort((a, b) => b.totalRequests - a.totalRequests);

const SectorMiniTable = ({ sectors }) => {
  if (!sectors || sectors.length === 0) {
    return <span>-</span>;
  }
  return (
    <table className="ltb-sector-mini-table">
      <thead>
        <tr>
          <th>Sector</th>
          <th>Requests</th>
        </tr>
      </thead>
      <tbody>
        {sectors.map((s) => (
          <tr key={s.sector}>
            <td>{s.sector}</td>
            <td>{formatNumber(s.totalRequests)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const LookToBook = () => {
  const [agentList, setAgentList] = useState(null);
  const [agent, setAgent] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [otaName, setOtaName] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState(null);
  const [selectedQuickRangeDays, setSelectedQuickRangeDays] = useState(null);
  const loggedInUserDetails = useSelector(
    (state) => state.loggedInUserDetails.loggedInUserDetails
  );

  useEffect(() => {
    if (loggedInUserDetails?.role === "admin") {
      fetchClientListForLoggedInAdmin();
    }
  }, [loggedInUserDetails]);

  useEffect(() => {
    const codes = new Set();
    tableData?.forEach((day) => day.airlines?.forEach((a) => codes.add(a.airline)));
    const airlineCodes = Array.from(codes);
    setSelectedAirline((prev) =>
      prev && airlineCodes.includes(prev) ? prev : airlineCodes[0] || null
    );
  }, [tableData]);

  const fetchClientListForLoggedInAdmin = () => {
    axios
      .get("https://api.aeroprime.in/crm-service/client/details", {
        headers: { Authorization: localStorage.getItem("AuthToken") },
      })
      .then((response) => setAgentList(response.data))
      .catch((error) => {
        if (error?.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/";
        }
      });
  };

  const applyQuickRange = (days) => {
    const end = dayjs();
    const start = dayjs().subtract(days - 1, "day");
    setStartDate(start);
    setEndDate(`${end.$y}-${end.$M + 1}-${end.$D}`);
    setSelectedQuickRangeDays(days);
  };

  const handleFetchReport = () => {
    if (!agent || !startDate || !endDate) return;
    setShowLoader(true);
    const startDt = `${startDate["$y"]}-${startDate["$M"] + 1}-${startDate["$D"]}`;
    axios
      .get(
        `https://api.aeroprime.in/crm-service/traffic/ota-matrix?clientId=${agent}&fromDate=${startDt}&toDate=${endDate}`,
        { headers: { Authorization: localStorage.getItem("AuthToken") } }
      )
      .then((response) => {
        const sortedData = [...(response.data?.data || [])].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        setOtaName(response.data?.otaName || "");
        setTableData(sortedData);
        setShowLoader(false);
      })
      .catch((error) => {
        setShowLoader(false);
        if (error?.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/";
        }
      });
  };

  const getAirlineCodes = () => {
    const codes = new Set();
    tableData?.forEach((day) => day.airlines?.forEach((a) => codes.add(a.airline)));
    return Array.from(codes);
  };

  const getChartableDaysForAirline = (airlineCode) =>
    (tableData || []).filter((day) => {
      const airlineEntry = day.airlines?.find((a) => a.airline === airlineCode);
      return !!airlineEntry?.totalSearches;
    });

  const buildAirlineMetricChartData = (airlineCode, metricKey, label) => {
    const days = getChartableDaysForAirline(airlineCode);
    const rows = days.map((day) => {
      const airlineEntry = day.airlines?.find((a) => a.airline === airlineCode);
      const rawValue = airlineEntry ? airlineEntry[metricKey] : null;
      return [day.date, rawValue === null || rawValue === undefined ? 0 : rawValue];
    });
    return [["Date", label], ...rows];
  };

  const getAggregatedSectorsForAirline = (airlineCode) => {
    const sectorTotals = {};
    tableData?.forEach((day) => {
      const airlineEntry = day.airlines?.find((a) => a.airline === airlineCode);
      airlineEntry?.topSectors?.forEach((s) => {
        sectorTotals[s.sector] = (sectorTotals[s.sector] || 0) + s.totalRequests;
      });
    });
    return Object.entries(sectorTotals).map(([sector, totalRequests]) => ({
      sector,
      totalRequests,
    }));
  };

  const getAirlineSummary = (airlineCode) => {
    let totalSearches = 0;
    let totalBookings = 0;
    let peakQps = 0;
    tableData?.forEach((day) => {
      const airlineEntry = day.airlines?.find((a) => a.airline === airlineCode);
      if (airlineEntry) {
        totalSearches += airlineEntry.totalSearches || 0;
        totalBookings += airlineEntry.totalBookings || 0;
        if (airlineEntry.peakQps && airlineEntry.peakQps > peakQps) {
          peakQps = airlineEntry.peakQps;
        }
      }
    });
    const searchesPerBooking = totalBookings > 0 ? totalSearches / totalBookings : null;
    return { totalSearches, totalBookings, searchesPerBooking, peakQps };
  };

  const sanitizeSheetName = (name) =>
    (name || "Airline").replace(/[:\\/?*[\]]/g, "").slice(0, 31) || "Airline";

  const handleDownloadReport = () => {
    if (!tableData || tableData.length === 0) return;
    const airlineCodes = getAirlineCodes();
    const workbook = XLSX.utils.book_new();

    const overviewRows = [
      [
        "Date",
        "OTA",
        "Total Searches",
        "Total Bookings",
        "Searches Per Booking",
        "Peak Requests Per Minute",
        "Peak Queries Per Second",
      ],
      ...tableData.map((day) => [
        day.date,
        otaName || "",
        day.totalSearches,
        day.totalBookings,
        roundValue(day.lookToBookRatio),
        day.peakRpm,
        roundValue(day.peakQps),
      ]),
    ];
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet(overviewRows),
      "Overview"
    );

    const usedSheetNames = new Set(["Overview"]);
    airlineCodes.forEach((code) => {
      const rows = [
        [
          "Date",
          "Total Searches",
          "Total Bookings",
          "Searches Per Booking",
          "Peak Requests Per Minute",
          "Peak Queries Per Second",
        ],
      ];
      tableData.forEach((day) => {
        const airlineEntry = day.airlines?.find((a) => a.airline === code);
        rows.push([
          day.date,
          airlineEntry?.totalSearches ?? "",
          airlineEntry?.totalBookings ?? "",
          roundValue(airlineEntry?.lookToBookRatio),
          airlineEntry?.peakRpm ?? "",
          roundValue(airlineEntry?.peakQps),
        ]);
      });

      const topSectors = filterSectors(getAggregatedSectorsForAirline(code));
      rows.push([]);
      rows.push(["Top Sectors (aggregated across selected range, >= 10 requests)"]);
      rows.push(["Sector", "Total Requests"]);
      topSectors.forEach((s) => {
        rows.push([s.sector, s.totalRequests]);
      });

      let sheetName = sanitizeSheetName(code);
      while (usedSheetNames.has(sheetName)) {
        sheetName = `${sheetName}_`.slice(0, 31);
      }
      usedSheetNames.add(sheetName);
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), sheetName);
    });

    const clientLabel = otaName || agentList?.[agent] || agent;
    const startLabel = startDate
      ? `${startDate["$y"]}-${startDate["$M"] + 1}-${startDate["$D"]}`
      : "";
    XLSX.writeFile(
      workbook,
      `LookToBookReport_${clientLabel}_${startLabel}_to_${endDate || ""}.xlsx`
    );
  };

  const airlineCodes = getAirlineCodes();
  const selectedAirlineSectors = selectedAirline
    ? filterSectors(getAggregatedSectorsForAirline(selectedAirline)).slice(0, 10)
    : [];
  const selectedAirlineTotalRequests = selectedAirline
    ? getAggregatedSectorsForAirline(selectedAirline).reduce(
        (sum, s) => sum + s.totalRequests,
        0
      )
    : 0;
  const selectedAirlineColor = selectedAirline
    ? getAirlineColor(selectedAirline, airlineCodes)
    : AIRLINE_PALETTE[0];
  const airlineSummary = selectedAirline ? getAirlineSummary(selectedAirline) : null;

  const chartHAxis = {
    title: "Date",
    slantedText: true,
    slantedTextAngle: 90,
    showTextEvery: 1,
    textStyle: { fontSize: 10 },
  };

  const chartArea = { left: 90, top: 30, width: "75%", height: "42%" };

  return (
    <div className="look-to-book-wrapper">
      <div className="Report-heading">SEARCHES PER BOOKING</div>
      <div className="Break-line-rp"></div>

      <div className="ltb-selection-section">
        {showLoader && <Loader hideLoader={false} />}
        <div className="ltb-client-selection-section">
          <StyledFormControl style={{ width: "250px", flexShrink: 0, flexGrow: 0, minWidth: "250px" }}>
            <Select
              id="ltb-client-select"
              value={agent || ""}
              onChange={(event) => {
                setAgent(event.target.value);
                setTableData(null);
              }}
              name="OTA / Client"
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <PlaceholderTypography>OTA / Client</PlaceholderTypography>;
                }
                return agentList?.[selected] || "";
              }}
              style={{
                color: "#000",
                fontSize: "15px",
                width: "250px",
                height: "55px",
              }}
              IconComponent={(props) => (
                <ExpandMoreIcon
                  {...props}
                  style={{ color: "#ff5722", marginRight: "8px" }}
                />
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    border: "1px solid #E5E2DA",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    marginTop: "8px",
                    borderRadius: "6px",
                    maxHeight: "200px",
                    maxWidth: "250px",
                    overflow: "auto",
                  },
                },
                MenuListProps: {
                  style: {
                    padding: 0,
                    maxHeight: "200px",
                    maxWidth: "250px",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  },
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                },
              }}
            >
              {agentList &&
                Object.keys(agentList).map((key) => (
                  <MenuItem
                    key={key}
                    value={key}
                    style={{
                      color: "#000",
                      fontWeight: "normal",
                      textAlign: "left",
                      paddingLeft: "10px",
                      paddingRight: "10px",
                    }}
                  >
                    {agentList[key]}
                  </MenuItem>
                ))}
            </Select>
          </StyledFormControl>
        </div>
        {tableData && tableData.length > 0 && (
          <div className="ltb-download-btn-wrapper">
            <StyledFormControl>
              <button className="download-btn" onClick={handleDownloadReport}>
                <img src={downloadSvg} alt="" className="downloadIcon" />
                Download Report
              </button>
            </StyledFormControl>
          </div>
        )}
      </div>

      <div className="ltb-dates-section">
        <div className="ltb-start-date-picker-section">
          <StyledFormControl style={{ width: "250px", flexShrink: 0, flexGrow: 0, minWidth: "250px" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer
                components={["DatePicker"]}
                sx={{
                  height: "60px",
                  width: "250px",
                  overflow: "hidden",
                  "& > div": { overflow: "hidden", width: "100%" },
                }}
              >
                <CustomDatePicker
                  value={startDate}
                  onChange={(val) => {
                    setStartDate(val);
                    setSelectedQuickRangeDays(null);
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </StyledFormControl>
        </div>
        <div className="ltb-end-date-picker-section">
          <StyledFormControl style={{ width: "250px", flexShrink: 0, flexGrow: 0, minWidth: "250px" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer
                components={["DatePicker"]}
                sx={{
                  height: "60px",
                  width: "250px",
                  overflow: "hidden",
                  "& > div": { overflow: "hidden", width: "100%" },
                }}
              >
                <CustomDatePicker
                  value={endDate ? dayjs(endDate) : null}
                  onChange={(val) => {
                    setEndDate(`${val["$y"]}-${val["$M"] + 1}-${val["$D"]}`);
                    setSelectedQuickRangeDays(null);
                  }}
                  minDate={startDate}
                  disabled={!startDate}
                />
              </DemoContainer>
            </LocalizationProvider>
          </StyledFormControl>
        </div>
        <div className="ltb-quick-range-section">
          {QUICK_RANGES.map((range) => (
            <Chip
              key={range.days}
              label={range.label}
              clickable
              onClick={() => applyQuickRange(range.days)}
              sx={{
                border: "1px solid #EF5443",
                backgroundColor: selectedQuickRangeDays === range.days ? "#EF5443" : "#ffffff",
                color: selectedQuickRangeDays === range.days ? "#ffffff" : "#EF5443",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: selectedQuickRangeDays === range.days ? "#EF5443" : "#fdeceb",
                },
              }}
            />
          ))}
        </div>
        <div className="ltb-fetch-btn-inline">
          <StyledFormControl>
            <button
              className="fetch-btn"
              onClick={handleFetchReport}
              disabled={!agent || !startDate || !endDate}
            >
              Fetch Report
            </button>
          </StyledFormControl>
        </div>
      </div>

      {tableData && tableData.length > 0 && (
        <>
          {airlineCodes.length > 0 && (
            <div className="ltb-airline-tabs">
              {airlineCodes.map((code) => {
                const color = getAirlineColor(code, airlineCodes);
                const isActive = selectedAirline === code;
                return (
                  <button
                    key={code}
                    className="ltb-airline-tab-btn"
                    style={{
                      borderColor: color,
                      backgroundColor: isActive ? color : "#ffffff",
                      color: isActive ? "#ffffff" : color,
                    }}
                    onClick={() => setSelectedAirline(code)}
                  >
                    {code}
                  </button>
                );
              })}
            </div>
          )}

          {selectedAirline && airlineSummary && (
            <div className="ltb-kpi-row">
              <div className="ltb-kpi-tile" style={{ borderTopColor: selectedAirlineColor }}>
                <div className="ltb-kpi-label">Total Searches</div>
                <div className="ltb-kpi-value">{formatNumber(airlineSummary.totalSearches)}</div>
              </div>
              <div className="ltb-kpi-tile" style={{ borderTopColor: selectedAirlineColor }}>
                <div className="ltb-kpi-label">Total Bookings</div>
                <div className="ltb-kpi-value">{formatNumber(airlineSummary.totalBookings)}</div>
              </div>
              <div className="ltb-kpi-tile" style={{ borderTopColor: selectedAirlineColor }}>
                <div className="ltb-kpi-label">Searches Per Booking</div>
                <div className="ltb-kpi-value">
                  {airlineSummary.searchesPerBooking != null
                    ? formatNumber(airlineSummary.searchesPerBooking, 2)
                    : "-"}
                </div>
              </div>
              <div className="ltb-kpi-tile" style={{ borderTopColor: selectedAirlineColor }}>
                <div className="ltb-kpi-label">Peak Queries Per Second</div>
                <div className="ltb-kpi-value">
                  {airlineSummary.peakQps > 0 ? formatNumber(airlineSummary.peakQps, 2) : "-"}
                </div>
              </div>
            </div>
          )}

          {selectedAirline && (
            <>
              <div className="ltb-charts-wrapper">
                <div
                  className="ltb-charts-heading"
                  style={{ borderLeft: `4px solid ${selectedAirlineColor}`, paddingLeft: "10px" }}
                >
                  {otaName ? `${otaName} — ${selectedAirline} Traffic` : `${selectedAirline} Traffic`}
                </div>
                <div className="ltb-charts">
                  <div className="ltb-chart">
                    <Chart
                      chartType="ColumnChart"
                      width="100%"
                      height="400px"
                      data={buildAirlineMetricChartData(selectedAirline, "totalSearches", "Total Searches")}
                      options={{
                        title: "Total Searches",
                        chartArea,
                        hAxis: chartHAxis,
                        vAxis: { title: "Total Searches", minValue: 0, format: "short" },
                        legend: { position: "none" },
                        colors: [selectedAirlineColor],
                      }}
                    />
                  </div>
                  <div className="ltb-chart">
                    <Chart
                      chartType="ColumnChart"
                      width="100%"
                      height="400px"
                      data={buildAirlineMetricChartData(selectedAirline, "totalBookings", "Total Bookings")}
                      options={{
                        title: "Total Bookings",
                        chartArea,
                        hAxis: chartHAxis,
                        vAxis: { title: "Total Bookings", minValue: 0, format: "short" },
                        legend: { position: "none" },
                        colors: [selectedAirlineColor],
                      }}
                    />
                  </div>
                  <div className="ltb-chart">
                    <Chart
                      chartType="ColumnChart"
                      width="100%"
                      height="400px"
                      data={buildAirlineMetricChartData(selectedAirline, "lookToBookRatio", "Searches Per Booking")}
                      options={{
                        title: "Searches Per Booking",
                        chartArea,
                        hAxis: chartHAxis,
                        vAxis: { title: "Searches Per Booking", minValue: 0, format: "short" },
                        legend: { position: "none" },
                        colors: [selectedAirlineColor],
                      }}
                    />
                  </div>
                  <div className="ltb-chart">
                    <Chart
                      chartType="ColumnChart"
                      width="100%"
                      height="400px"
                      data={buildAirlineMetricChartData(selectedAirline, "peakQps", "Queries Per Second")}
                      options={{
                        title: "Queries Per Second",
                        chartArea,
                        hAxis: chartHAxis,
                        vAxis: { title: "Queries Per Second", minValue: 0, format: "short" },
                        legend: { position: "none" },
                        colors: [selectedAirlineColor],
                      }}
                    />
                  </div>
                </div>

                <div
                  className="ltb-sector-table-heading"
                  style={{ borderLeft: `4px solid ${selectedAirlineColor}`, paddingLeft: "10px" }}
                >
                  {`Top Sectors — ${selectedAirline} (aggregated across selected range) — Total Requests in last ${
                    tableData.length
                  } day${tableData.length === 1 ? "" : "s"}: ${formatNumber(selectedAirlineTotalRequests)}`}
                </div>
                <TableContainer component={Paper} style={{ borderRadius: "10px", overflow: "auto" }}>
                  <Table size="small" aria-label="airline-sector-table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell align="left">Sector</StyledTableCell>
                        <StyledTableCell align="left">Total Requests</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedAirlineSectors.length > 0 ? (
                        selectedAirlineSectors.map((s) => (
                          <TableRow key={s.sector}>
                            <TableCell align="left">{s.sector}</TableCell>
                            <TableCell align="left">{formatNumber(s.totalRequests)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2} align="center">
                            No sector data
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>

              <div
                className="ltb-date-wise-heading"
                style={{ borderLeft: `4px solid ${selectedAirlineColor}`, paddingLeft: "10px" }}
              >{`Date Wise Insights — ${selectedAirline}`}</div>
              <TableContainer
                component={Paper}
                style={{ borderRadius: "10px", overflow: "auto", maxHeight: "520px" }}
              >
                <Table aria-label="date-wise-insights-table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell align="left">Date</StyledTableCell>
                      <StyledTableCell align="left">Total Searches</StyledTableCell>
                      <StyledTableCell align="left">Total Bookings</StyledTableCell>
                      <StyledTableCell align="left">Searches Per Booking</StyledTableCell>
                      <StyledTableCell align="left">Peak Requests Per Minute</StyledTableCell>
                      <StyledTableCell align="left">Peak Queries Per Second</StyledTableCell>
                      <StyledTableCell align="left">Top Sectors</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.map((day) => {
                      const airlineEntry = day.airlines?.find(
                        (a) => a.airline === selectedAirline
                      );
                      const daySectors = filterSectors(airlineEntry?.topSectors).slice(0, 5);
                      return (
                        <TableRow key={day.date}>
                          <TableCell align="left">{day.date}</TableCell>
                          <TableCell align="left">{displayMetric(airlineEntry?.totalSearches)}</TableCell>
                          <TableCell align="left">{displayMetric(airlineEntry?.totalBookings)}</TableCell>
                          <TableCell align="left">{displayMetric(airlineEntry?.lookToBookRatio, 2)}</TableCell>
                          <TableCell align="left">{displayMetric(airlineEntry?.peakRpm)}</TableCell>
                          <TableCell align="left">{displayMetric(airlineEntry?.peakQps, 2)}</TableCell>
                          <TableCell align="left">
                            <SectorMiniTable sectors={daySectors} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
      {tableData && tableData.length === 0 && (
        <div className="No-Records">No Records Found!!</div>
      )}
    </div>
  );
};

export default LookToBook;
